// Per-scene undo/redo of a layer list, shared by the overlay editor and the call studio editor. Each scene keeps a
// list of layer-list snapshots and the index of the current one; undoing or redoing makes the server match a
// snapshot again (deleting, recreating and patching layers).
//
// The consuming component owns: `activeScene` (with `.layers`), `selectedId`, `error`, `$api`, and a
// `layersApiBase(scene)` method returning the API path of that scene's layers ("overlays/1/scenes/2/layers").
export default {
	data() { return {
		// histVersion nudges canUndo/canRedo, since the history itself isn't reactive (it can get large).
		histVersion: 0,
		historyBusy: false,
	}},
	computed: {
		canUndo() {
			this.histVersion; // eslint-disable-line no-unused-expressions
			const h = this.activeScene && this._hist[this.activeScene.id];
			return !!h && h.index > 0 && !this.historyBusy;
		},
		canRedo() {
			this.histVersion; // eslint-disable-line no-unused-expressions
			const h = this.activeScene && this._hist[this.activeScene.id];
			return !!h && h.index < h.states.length - 1 && !this.historyBusy;
		},
	},
	created() {
		this._hist = {};
	},
	methods: {
		snapshotLayers(scene) {
			return [ ...scene.layers ].sort((a, b) => a.sort_order - b.sort_order).map(l => JSON.parse(JSON.stringify({
				id: l.id, type: l.type, name: l.name, layer_color: l.layer_color, visible: l.visible, locked: l.locked,
				x: l.x, y: l.y, w: l.w, h: l.h, rotation: l.rotation, props: l.props,
			})));
		},
		ensureHistory(scene) {
			if(!this._hist[scene.id]) this._hist[scene.id] = { states: [ this.snapshotLayers(scene) ], index: 0 };
			this.histVersion++;
		},
		// Record the scene's layers as they are now, after a change has been made.
		commitHistory(scene = this.activeScene) {
			if(!scene || this.historyBusy) return;
			const h = this._hist[scene.id];
			if(!h) return;
			const state = this.snapshotLayers(scene);
			if(JSON.stringify(state) === JSON.stringify(h.states[h.index])) return;
			h.states.splice(h.index + 1);
			h.states.push(state);
			if(h.states.length > 100) h.states.shift();
			h.index = h.states.length - 1;
			this.histVersion++;
		},
		undo() { return this.stepHistory(-1); },
		redo() { return this.stepHistory(1); },
		async stepHistory(direction) {
			const scene = this.activeScene;
			const h = scene && this._hist[scene.id];
			if(!h || this.historyBusy) return;
			const index = h.index + direction;
			if(index < 0 || index >= h.states.length) return;
			this.historyBusy = true;
			try {
				await this.applyLayerState(scene, h, h.states[index]);
				h.index = index;
			} catch(e) {
				this.error = e;
			} finally {
				this.historyBusy = false;
				this.histVersion++;
			}
		},
		// Make the scene's layers match a snapshot: delete what it lacks, recreate what it has that is gone, patch the rest.
		async applyLayerState(scene, history, target) {
			const base = this.layersApiBase(scene);
			const current = new Map(scene.layers.map(l => [ l.id, l ]));
			const wanted = new Set(target.map(l => l.id));
			for(const layer of scene.layers) {
				if(!wanted.has(layer.id)) await this.$api.delete(`${base}/${layer.id}`);
			}
			const result = [];
			for(const t of target) {
				const body = { name: t.name, layerColor: t.layer_color, visible: t.visible, locked: t.locked, x: t.x, y: t.y, w: t.w, h: t.h, rotation: t.rotation, props: t.props };
				let layer = current.get(t.id);
				if(!layer) {
					const created = await this.$api.post(base, { type: t.type, ...body });
					// The recreated layer has a new id; keep every snapshot pointing at it.
					for(const state of history.states) for(const l of state) if(l.id === t.id) l.id = created.id;
					layer = { ...JSON.parse(JSON.stringify(t)), id: created.id, scene_id: scene.id, sort_order: 0 };
				} else if(JSON.stringify(this.snapshotLayers({ layers: [ layer ] })[0]) !== JSON.stringify(t)) {
					await this.$api.patch(`${base}/${layer.id}`, body);
					Object.assign(layer, JSON.parse(JSON.stringify(t)));
				}
				result.push(layer);
			}
			const orderChanged = result.some((l, i) => l.sort_order !== i);
			result.forEach((l, i) => { l.sort_order = i; });
			if(orderChanged || result.length !== current.size) await this.$api.post(`${base}/reorder`, { orderedLayerIds: result.map(l => l.id) });
			scene.layers = result;
			if(!result.some(l => l.id === this.selectedId)) this.selectedId = null;
		},
	},
};
